"use client";

import React from "react";
import { ClipboardCheck, CalendarClock, Zap, Truck, MapPin, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface AccessCard {
  icon: React.ElementType;
  title: string;
  description: string;
}
const accessCards: AccessCard[] = [{
  icon: ClipboardCheck,
  title: "Site Access Review",
  description: "Placement area, gates, clearances, surface conditions, and other site-access considerations are reviewed for the requested trailer."
}, {
  icon: CalendarClock,
  title: "Delivery Coordination",
  description: "Requested dates, delivery location, intended use, preferred trailer size, and placement details are reviewed with each quote."
}, {
  icon: Zap,
  title: "Power and Fuel Planning",
  description: "Available site power and trailer-specific power or fuel requirements are confirmed individually before the rental."
}, {
  icon: Truck,
  title: "53-Foot Reefer Requirements",
  description: "Loading, delivery, placement, site access, power or fuel requirements, and suitability for the 53-foot reefer are reviewed individually."
}];
const southernNevadaAreas = ["Las Vegas", "North Las Vegas", "Henderson", "Paradise", "Spring Valley", "Summerlin", "Boulder City", "Enterprise"];
export default function NevadaAccess() {
  return <section id="southernnevadadeliveryaccess" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4">
            Delivery & Site Access
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Southern Nevada delivery and site access
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Site access, delivery details, and equipment requirements are reviewed individually with each quote across Southern Nevada.
          </p>
        </div>

        {/* Four Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {accessCards.map((item, i) => {
          const Icon = item.icon;
          return <Card key={item.title} data-index={i} className="border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="w-10 h-10 rounded-md bg-accent flex items-center justify-center text-accent-foreground mb-3">
                    <Icon className="size-5" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-card-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed mt-2">
                    {item.description}
                  </CardDescription>
                </CardHeader>
              </Card>;
        })}
        </div>

        {/* Territory & Scope Details */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-7 bg-muted/40 border border-border rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3 text-foreground font-semibold">
              <MapPin className="size-5 text-primary" />
              <h3 className="text-lg">Service territory</h3>
            </div>
            <p className="text-sm md:text-base text-muted-foreground mb-6 leading-relaxed">
              Fridge Fleet serves Las Vegas and listed Southern Nevada communities. Delivery coverage, trailer availability, and requirements are confirmed with each quote.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {southernNevadaAreas.map((area, aIdx) => <div key={area} data-index={aIdx} className="bg-card border border-border rounded-md px-3 py-2 text-xs md:text-sm font-medium text-foreground text-center">
                  {area}
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-5 bg-card border border-border rounded-xl p-6 md:p-8 flex flex-col justify-between h-full">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Ready to check your site?
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Share your location, timing, and trailer requirements to review site access and delivery details for your rental.
              </p>
            </div>
            <Button asChild size="default" className="w-full sm:w-auto self-start">
              <Link to="/ContactRequestAQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}